/**
 * API Client Service
 * Provides a centralized HTTP client with authentication and error handling
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export interface ValidationDetail {
  field: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    public code: string,
    public message: string,
    public statusCode?: number,
    public details?: ValidationDetail[]
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class ApiClient {
  private baseUrl: string;
  private onUnauthorized?: () => void;

  constructor(baseUrl: string = API_BASE_URL) {
    // Ensure base URL has /api/v1 suffix
    this.baseUrl = baseUrl.endsWith('/api/v1') ? baseUrl : `${baseUrl}/api/v1`;
  }

  /**
   * Set handler for 401 Unauthorized responses
   * This allows the presentation layer to handle auth errors (e.g., redirect to login)
   * without the API client knowing about routes
   */
  setUnauthorizedHandler(handler: () => void) {
    this.onUnauthorized = handler;
  }

  /**
   * Make HTTP request
   * Automatically includes service provider auth token for /provider/* endpoints
   * Automatically includes applicant auth token for /jobs/* and /projects/* endpoints
   */
  async request<T>(
    endpoint: string,
    options?: RequestInit
  ): Promise<T> {
    // Build headers
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(options?.headers || {}),
    };

    // Auto-include service provider token for /provider/* endpoints
    if (endpoint.startsWith('/provider')) {
      const token = localStorage.getItem('sp_auth_token');
      if (token) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
      }
    }

    // Auto-include applicant token for /jobs/*, /projects/*, and /auth/me endpoints
    if (endpoint.startsWith('/jobs') || endpoint.startsWith('/projects') || endpoint === '/auth/me') {
      const token = localStorage.getItem('applicant_auth_token');
      if (token) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
      }
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    // Handle non-OK responses
    if (!response.ok) {
      // Notify presentation layer of 401 errors (let it handle redirect logic)
      if (response.status === 401 && this.onUnauthorized) {
        this.onUnauthorized();
      }

      let errorData;
      try {
        errorData = await response.json();
      } catch {
        throw new ApiError(
          'NETWORK_ERROR',
          `HTTP ${response.status}: ${response.statusText}`,
          response.status
        );
      }

      // Handle nested error structure from backend: { success: false, error: { code, message, details } }
      const errorInfo = errorData.error || errorData;

      throw new ApiError(
        errorInfo.code || 'UNKNOWN_ERROR',
        errorInfo.message || 'An unknown error occurred',
        response.status,
        errorInfo.details // Include validation details if present
      );
    }

    // Handle empty responses (204 No Content)
    if (response.status === 204) {
      return null as T;
    }

    return response.json();
  }

  /**
   * GET request
   */
  get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'GET',
    });
  }

  /**
   * POST request
   */
  post<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PUT request
   */
  put<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PATCH request
   */
  patch<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * DELETE request
   */
  delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'DELETE',
    });
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

/**
 * Permit API functions
 */
import type { Permit, CreatePermitRequest, PermitsResponse, PermitResponse } from '../types/job';

export const permitApi = {
  /**
   * Get all permits for a job
   */
  async getPermits(jobId: string): Promise<Permit[]> {
    const response = await apiClient.get<PermitsResponse>(`/jobs/${jobId}/permits`);
    return response.permits;
  },

  /**
   * Create a new permit
   */
  async createPermit(jobId: string, data: CreatePermitRequest): Promise<Permit> {
    const response = await apiClient.post<PermitResponse>(
      `/jobs/${jobId}/permits`,
      data
    );
    return response.permit;
  },

  /**
   * Delete a permit
   */
  async deletePermit(jobId: string, permitId: string): Promise<void> {
    await apiClient.delete(`/jobs/${jobId}/permits/${permitId}`);
  },
};